'use client'

import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'

export default function ModalDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  loading = false,
  showCancel = true,
  onConfirm,
  onClose,
}) {
  
  const handleClose = () => {
    if (onClose) onClose()
  }

  const handleConfirm = async () => {
    if (onConfirm) {
      try {
        await onConfirm()
        if (onClose) onClose()
      } catch (error) {
        // Error is handled by the onConfirm callback (toast notification)
        // Keep modal open so user can see the error and potentially retry
      }
    } else {
      if (onClose) onClose()
    }
  }

  const isDanger = variant === 'danger'

  return (
    <Dialog open={open} onClose={handleClose} className="relative z-[2000]">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in z-[2000]"
        />

        <div className="fixed inset-0 z-[2000] w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-white dark:bg-slate-800 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div className="px-4 pb-4 pt-5 sm:p-6 sm:pb-4 bg-white dark:bg-slate-800">
                <div className="sm:flex sm:items-start">
                  <div
                    className={`mx-auto flex size-12 shrink-0 items-center justify-center rounded-full sm:mx-0 sm:size-10 ${
                      isDanger 
                        ? 'bg-red-100 dark:bg-red-900/30'
                        : 'bg-indigo-100 dark:bg-indigo-900/30'
                    }`}
                  >
                    <ExclamationTriangleIcon
                      aria-hidden="true"
                      className={`size-6 ${
                        isDanger 
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-indigo-600 dark:text-indigo-400'
                      }`}
                    />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <DialogTitle as="h3" className="text-base font-semibold text-gray-900 dark:text-slate-100">
                      {title}
                    </DialogTitle>
                    <div className="mt-2">
                      {typeof description === 'string' ? (
                      <p className="text-sm text-gray-500 dark:text-slate-400">
                        {description}
                      </p>
                      ) : (
                        <div className="text-sm text-gray-500 dark:text-slate-400">
                          {description}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 bg-gray-50 dark:bg-slate-800">
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={loading}
                  className={`inline-flex w-full justify-center items-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm ${showCancel ? 'sm:ml-3' : ''} sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed ${
                    isDanger 
                      ? 'bg-red-600 hover:bg-red-500'
                      : 'bg-indigo-600 hover:bg-indigo-500'
                  }`}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    confirmLabel
                  )}
                </button>
                {showCancel && (
                <button
                  type="button"
                  data-autofocus
                  onClick={handleClose}
                  disabled={loading}
                  className="mt-3 inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm ring-1 ring-inset sm:mt-0 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 ring-gray-300 dark:ring-white/10 hover:bg-gray-50 dark:hover:bg-slate-600"
                >
                  {cancelLabel}
                </button>
                )}
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
  )
}
